<script setup>
import {onMounted, ref} from 'vue'
const emit = defineEmits(['change'])

const props = defineProps({
  sm: {
    type: [Number, String],
    default: 2,
  },
  md: {
    type: [Number, String, Boolean],
    default: false,
  },
  preview: {
    type: Boolean,
    default: true,
  },
  scroll: {
    type: Boolean,
    default: true,
  },
  dots: {
    type: Boolean,
    default: false,
  },
  arrows: {
    type: Boolean,
    default: false,
  },
})

const slider = ref('')
const items = ref('')
const activeIndex = ref(0)
const numberOfSlides = ref(0)

function goToSlide(index) {
  if (index >= numberOfSlides.value) {
    index = 0
  }
  if (index < 0) {
    index = numberOfSlides.value - 1
  }

  items.value?.children?.[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
}

function handleIntersection(entries) {
  entries.map((entry) => {
    if (entry.isIntersecting) {
      activeIndex.value = Array.from(entry.target.parentNode.children).indexOf(entry.target)
      emit('change', activeIndex.value)
    }
  })
}

onMounted(() => {
  const observer = new IntersectionObserver(handleIntersection, {
    threshold: 0.8,
  })
  Array.from(items.value?.children)?.forEach(slide => observer.observe(slide))
  numberOfSlides.value = items.value?.children?.length || 0
})
</script>

<template>
  <div class="slider__wrapper">
    <div class="slider" :class="{'has-preview': preview, 'hide-scroll': !scroll}" :data-columns="sm" :data-columns-md="md" ref="slider">
      <div class="slider__items" ref="items">
        <slot />
      </div>
    </div>

    <div class="slider__dots" v-if="dots && numberOfSlides > 1">
      <div
          class="slider__dot"
          :class="{'slider__dot--active': activeIndex === (i - 1)}"
          v-for="i in numberOfSlides"
          :key="i"
          @click="goToSlide(i - 1)"
      ></div>
    </div>

    <div class="slider__arrows" v-if="arrows && numberOfSlides > 1">
      <a href="#" class="slider__arrow slider__arrow--prev" @click.prevent="goToSlide(activeIndex - 1)">
        <slot name="arrowPrev">
          <svg xmlns="http://www.w3.org/2000/svg" width="44" height="45" viewBox="0 0 44 45" fill="none">
            <rect opacity="0.6" x="44" y="44.5" width="44" height="44" rx="22" transform="rotate(-180 44 44.5)" fill="#393632" fill-opacity="0.15"/>
            <path d="M28.0498 13.1484L18.4248 22.7734L28.0498 32.3984" stroke="#8C8C8C" stroke-width="3"/>
          </svg>
        </slot>
      </a>

      <a href="#" class="slider__arrow slider__arrow--next" @click.prevent="goToSlide(activeIndex + 1)">
        <slot name="arrowNext">
          <svg xmlns="http://www.w3.org/2000/svg" width="44" height="45" viewBox="0 0 44 45" fill="none">
            <rect opacity="0.6" x="44" y="44.5" width="44" height="44" rx="22" transform="rotate(-180 44 44.5)" fill="#393632" fill-opacity="0.15"/>
            <path d="M18.4248 32.3984L28.0498 22.7734L18.4248 13.1484" stroke="#8C8C8C" stroke-width="3"/>
          </svg>
        </slot>
      </a>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'sass:math';

$gap: 20px !default;
$breakpoint: 769px !default;

.slider {
  overflow-x: hidden;

  &.hide-scroll .slider__items {
    -ms-overflow-style: none;  /* Internet Explorer 10+ */
    scrollbar-width: none;  /* Firefox */

    &::-webkit-scrollbar {
      display: none;  /* Safari and Chrome */
    }
  }

  & > .slider__items > :deep(*) {
    flex: none;
    scroll-snap-align: start; /* latest (Chrome 69+) */
    scroll-snap-coordinate: 0% 0%; /* older (Firefox/IE) */
    -webkit-scroll-snap-coordinate: 0% 0%; /* older (Safari) */
    display: block;
    min-width: 0;
  }

  &:not([data-columns="1"]) > .slider__items > :deep(*) {
    padding: 0 $gap 0 0;
  }

  & > .slider__items {
    overflow-x: scroll;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory; /* Chrome Canary */
    scroll-snap-type: mandatory; /* Firefox */
    -ms-scroll-snap-type: mandatory; /* IE/Edge */
    -webkit-scroll-snap-type: mandatory; /* Safari */
    -webkit-scroll-snap-destination: 0% 0%;
    -webkit-overflow-scrolling: touch; /* important for iOS */
    display: flex;
    padding: $gap 0;
  }

  @media screen and (min-width: $breakpoint) {
    &:not([data-columns-md]),
    &[data-columns-md="false"] {
      overflow-x: visible;

      & > .slider__items {
        overflow-x: visible;
        gap: $gap;
        padding-right: 0!important;

        & > :deep(*) {
          flex: 1;
          padding: 0!important;
        }
      }
    }
  }

  @for $i from 1 through 12 {
    &[data-columns="#{$i}"] {
      & > .slider__items {
        scroll-snap-points-y: repeat(math.percentage(math.div(1, $i)));
      }

      & > .slider__items > :deep(*) {
        width: calc(#{math.percentage(math.div(1, $i))} - #{math.div($gap * ($i - 1), $i)} + 0.1px);
      }

      &.has-preview {
        & > .slider__items {
          padding-right: calc($gap / 2);
        }

        & > .slider__items > :deep(*) {
          width: calc(#{math.percentage(math.div(1, $i))} - #{math.div(calc($gap / 2) * $i, $i)});
          padding: 0 0 0 $gap;
        }
      }
    }

    @media screen and (min-width: $breakpoint) {
      &[data-columns-md="#{$i}"] {
        & > .slider__items {
          scroll-snap-points-y: repeat(math.percentage(math.div(1, $i)));
        }

        & > .slider__items > :deep(*) {
          width: calc(#{math.percentage(math.div(1, $i))} - #{math.div($gap * ($i - 1), $i)} + 0.1px)!important;
        }
      }
    }
  }
}

.slider__wrapper {
  position: relative;
}

.slider__arrows {
  .slider__arrow {
    position: absolute;
    top: 50%;

    &.slider__arrow--prev {
      left: -90px;
    }

    &.slider__arrow--next {
      right: -90px;
    }
  }
}

.slider__dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;

  .slider__dot {
    background-color: var(--Neutral-200, #E4E1D7);
    width: 13px;
    height: 13px;
    border-radius: 50%;
    cursor: pointer;

    &.slider__dot--active {
      background-color: var(--Orange---terracotta, #DB7E5A);
    }
  }
}
</style>
